import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { OidcConfigurationNameContext } from "../../contexts/OidcConfigurationNameContext";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import IChatOptions from "../../types/interfaces/IChatOptions";
import { ChatOptionsContext } from "../../contexts/ChatOptionsContext";
import { getChatOptions } from "../../functions/requests/getChatOptions";
import { useAuthorizationError } from "../../contexts/AuthorizationErrorContext";

export const ChatOptionsProvider = ({ children }: { children: ReactNode }) => {
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const { setAuthError } = useAuthorizationError();
  const [chatOptions, setChatOptions] = useState<IChatOptions>({
    default_system_prompt: "",
    help_text: "",
    show_rag: false,
    group: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const reloadOptions = useCallback(async () => {
    if (!oidcAccessToken?.accessToken) {
      console.log("ChatOptionsProvider: No access token available yet");
      return;
    }
    console.log("ChatOptionsProvider: Making API call with token:", oidcAccessToken.accessToken?.substring(0, 50) + "...");
    setLoading(true);
    setError(null);
    try {
      const chatOptionsResponse = await getChatOptions(oidcAccessToken);
      setChatOptions(chatOptionsResponse);
      console.log("ChatOptionsProvider: Successfully loaded chat options");
    } catch (err) {
      console.error("ChatOptionsProvider: API call failed:", err);
      
      // Enhanced error detection for 401 unauthorized errors
      let isAuthError = false;
      let errorMessage = "Unknown error";
      
      if (err instanceof Error) {
        errorMessage = err.message;
        console.log("ChatOptionsProvider: Error message:", errorMessage);
        
        // Check multiple ways a 401 error might be represented
        const lowerErrorMessage = errorMessage.toLowerCase();
        isAuthError = lowerErrorMessage.includes('401') || 
                     lowerErrorMessage.includes('unauthorized') ||
                     lowerErrorMessage.includes('access denied') ||
                     lowerErrorMessage.includes('forbidden');
      } else if (typeof err === 'object' && err !== null) {
        // Check if it's a fetch response error
        const errObj = err as any;
        if (errObj.status === 401 || errObj.statusCode === 401) {
          isAuthError = true;
          errorMessage = `HTTP ${errObj.status || errObj.statusCode}: Unauthorized`;
        }
        console.log("ChatOptionsProvider: Error object:", errObj);
      }
      
      if (isAuthError) {
        console.log("ChatOptionsProvider: 🚨 DETECTED AUTHORIZATION ERROR - Setting in context");
        console.log("ChatOptionsProvider: Error details:", {
          message: errorMessage,
          endpoint: '/chat-config/options',
          provider: oidcConfigurationName,
          timestamp: new Date().toISOString()
        });
        
        // Set the authorization error in context
        setAuthError({
          message: errorMessage,
          endpoint: '/chat-config/options',
          provider: oidcConfigurationName,
          timestamp: new Date().toISOString()
        });
        return; // Don't set error state, we're handling this via context
      } else {
        console.log("ChatOptionsProvider: Not an authorization error, setting normal error state");
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  }, [oidcAccessToken, oidcConfigurationName, setAuthError]);

  useEffect(() => {
    void reloadOptions();
  }, [reloadOptions]);

  return (
    <ChatOptionsContext.Provider
      value={{
        chatOptions: chatOptions,
        loading: loading,
        error: error,
        reload: reloadOptions,
        name: "chat options",
      }}
    >
      {children}
    </ChatOptionsContext.Provider>
  );
};
